(ns minorodata.components.tables.details
  (:require ["react" :as react]
            [clojure.string :as str]
            [lucide-react :refer [ArrowLeft RefreshCw]]
            [minorodata.app.handlers :refer [dispatch]]
            [minorodata.app.utils :as app.utils :refer [format-transform-status]]
            [ui.data-grid.preview :as data-grid-preview]
            [minorodata.components.spinner.core :refer [spinner-lucide]]
            [minorodata.components.sql-console.utils :refer [generate-columns
                                                              generate-select]]
            [minorodata.components.tables.core :refer [init-tables]]
            [minorodata.sql-console.api :as sql-console.api]
            [minorodata.utils :as utils :refer [find-first]]
            [re-posh.core :as re-posh]
            [reagent.core :as reagent]
            [reitit.frontend.easy :as rt.easy]
            [ui.button :refer [button]]
            [ui.date-time :refer [date-tooltip]]
            [ui.dialog :refer [dialog-content dialog-root dialog-title]]
            [ui.docs-panel :as docs-panel]
            [ui.modal :as modal]
            [ui.page :refer [page-content page-controls page-header page-root
                             page-title]]
            [ui.table :refer [tbody td th thead tr]]
            [ui.utils :refer [clsx]]))

(defn ->qualified-name [catalog schema table]
  (->> [catalog schema table]
       (filter identity)
       (str/join ".")))

(defn valid-identifier? [s]
  (boolean (and (string? s) (re-matches #"[A-Za-z0-9_$]+(-[A-Za-z0-9_$]+)*" s))))

(defn preview-in-console [catalog schema table columns]
  (when (every? valid-identifier? (filter identity [catalog schema table]))
    (dispatch [:sql-console-modal.events/open-new-tab-with-code
               (generate-select
                {:cols  (generate-columns columns)
                 :table (->qualified-name catalog schema table)})])))

(defn go-to-tables []
  (dispatch [:app.events/navigate :tables]))

(defn sample-query [{:keys [catalog schema table]}]
  (str "SELECT * FROM " catalog "." schema "." table " LIMIT 1000;"))

(defn job-status-display [status]
  (let [status-config (case status
                        (:failed :fail) {:color "bg-kleene-text-error" :title "Failed"}
                        :success {:color "bg-kleene-text-success" :title "Finished"}
                        :running {:color "bg-kleene-text-secondary" :title "Running"}
                        :starting {:color "bg-kleene-text/50" :title "Starting"}
                        :pending {:color "bg-kleene-text/50" :title "Pending"}
                        {:color "bg-kleene-text/50" :title status})]
    (when (some? status)
      [:div {:class "inline-block align-middle"}
       [:div {:title   (:title status-config)
              :data-cy (str "job-status-"
                            (if (some? status)
                              (name status)
                              "unknown"))
              :class (clsx "h-3 w-3 rounded-full" (:color status-config))}]])))

(defn use-query [code]
  (let [[response set-response] (react/useState nil)]
    (react/useEffect
     (fn []
       (when code
         (dispatch [:sql-console.api/post-query {:query code}
                    {:on-success    #(set-response {:headers (ffirst (:data %))
                                                    :rows    (next (first (:data %)))})
                     :skip-console? true}]))
       (fn []))
     #js [code])
    response))

(defn use-sample [props]
  (let [query (sample-query props)]
    (use-query query)))

(defn last-altered-query [{:keys [warehouse catalog schema table]}]
  (case warehouse
    "redshift"
    (str "
  SELECT max(endtime) as last_altered
    FROM STL_INSERT
    JOIN pg_catalog.pg_class c ON tbl = c.oid
    JOIN pg_catalog.pg_namespace n ON n.oid = c.relnamespace
   WHERE rows > 0 AND n.nspname = '" schema "' and c.relname = '" table "'
GROUP BY n.nspname, c.relname;")

    (str "
  SELECT last_altered
    FROM " catalog ".information_schema.tables
   WHERE table_schema = '" schema "'
     AND table_name = '" table "';")))

(defn use-get-latest-update [props]
  (let [warehouse @(re-posh/subscribe [:warehouse-data/prop :warehouse-data/sink-type])]
    (use-query
     (when warehouse
       (last-altered-query (assoc props :warehouse warehouse))))))


(defn- extracts-list [{:keys [sources]}]
  (let [timezone @(re-posh/subscribe [:app.subs/timezone])
        {sources-data :sources connectors :connectors}
        @(re-posh/subscribe [:connections.subs/sources-list-view])]
    [:div
     [docs-panel/section-title "Extracts"]

     (if (empty? sources)
       "No data"
       [:div {:class "flex flex-col gap-2"}
        (doall
         (for [{:keys [name extract-id source-id source-name updated-at updated-by last-run-status last-run-at] :as extract} sources]
           (let [user        @(re-posh/subscribe [:searchable-user/pull [:searchable-user/id updated-by]])
                 source-data (find-first #(= source-id (:source/source-id %)) sources-data)
                 source-data (app.utils/find-connector-by-type-and-version connectors
                                                                           (:source/source-type source-data)
                                                                           (get-in source-data [:source/source :version]))]
             ^{:key name}
             [docs-panel/details
              [docs-panel/details-title
               [:div {:class "flex justify-between items-center gap-2"}
                [:a {:class "underline"
                     :href   (rt.easy/href :extract-details
                                           {:extract-id extract-id
                                            :source-id source-id})}
                 name]
                (when (:image-source source-data)
                  [:img {:loading "lazy"
                         :title   (:source/source-type source-data)
                         :src     (app.utils/maybe-local-url (:image-source source-data))
                         :width   60
                         :height  20
                         :class   "object-contain bg-kleene-background border-none"}])]]
              [docs-panel/details-content
               [:div
                [ui.table/table {:style {"--background" "transparent"}
                                 :class "text-sm"}
                 [thead
                  [tr
                   [th {:class "px-2 py-0.5"} "Source"]
                   [th {:class "px-2 py-0.5"} "Schedule"]
                   [th {:class "px-2 py-0.5 w-48"} "Last run"]
                   [th {:class "px-2 py-0.5"} "Updated"]
                   [th {:class "px-2 py-0.5"} "Updated by"]]]
                 [tbody
                  [tr
                   [td {:class "px-2 py-0.5"}
                    [:a {:class "underline"
                         :href  (rt.easy/href :source-details {:source-id source-id})}
                     source-name]]
                   [td {:class "px-2 py-0.5"} (app.utils/extract-schedule extract)]
                   [td {:class "px-2 py-0.5"} (if last-run-at
                                                [:div {:class "flex items-center gap-2"}
                                                 [job-status-display (keyword last-run-status)]
                                                 [date-tooltip {:date last-run-at :timezone timezone}]]
                                                "No data")]
                   [td {:class "px-2 py-0.5"} [date-tooltip {:date updated-at :timezone timezone}]]
                   [td {:class "px-2 py-0.5"} (:searchable-user/email user)]]]]]]])))])]))

(defn transforms-list [{:keys [transforms]}]
  (let [timezone @(re-posh/subscribe [:app.subs/timezone])
        statuses @(re-posh/subscribe [:transform-status-list/prop :transform-status-list/statuses])]

    (react/useEffect
     (fn []
       (dispatch [:connections.api/fetch-transform-statuses])
       (fn []))
     #js [])

    [:div
     [docs-panel/section-title "Transforms"]

     (if (empty? transforms)
       "No data"
       [:div {:class "flex flex-col gap-2"}
        (doall
         (for [{:keys [group-name transform-id master-id group-id created-at updated-by] :as t} transforms]
           (let [user   @(re-posh/subscribe [:searchable-user/pull [:searchable-user/id updated-by]])
                 status (utils/find-first #(= (:transform-id %) transform-id) statuses)]
             ^{:key t}
             [docs-panel/details
              [docs-panel/details-title
               [:div {:class "flex justify-between gap-2"}
                (:name t)
                [:a {:class "underline"
                     :href  (rt.easy/href :dynamic-docs-details {:entity-id master-id})} "Open in data docs"]]]
              [docs-panel/details-content
               [:div
                [ui.table/table {:style {"--background" "transparent"}
                                 :class "text-sm"}
                 [thead
                  [tr
                   [th {:class "w-48"} "Group"]
                   [th "Created"]
                   [th "Created by"]
                   [th "Schedule"]
                   [th "Last run"]]]
                 [tbody
                  [tr
                   [td [:a {:href (rt.easy/href :transforms {:group-id group-id})
                            :class "underline"}
                        group-name]]
                   [td [date-tooltip {:date created-at :timezone timezone}]]
                   [td (:searchable-user/email user)]
                   [td (app.utils/transform-schedule t)]
                   [td (if status
                         (format-transform-status timezone status)
                         "No data")]]]]]]])))])]))

(defn sample-rows [{:keys [catalog schema table]}]
  (let [sample (use-sample {:catalog catalog
                            :schema  schema
                            :table   table})
        dark-mode? @(re-posh/subscribe [:app/prop :app/dark-mode?])]
    [:div {:class "col-span-2"}
     [docs-panel/section-title "Table preview"]

     (if (empty? sample)
       "No data"
       [:f> data-grid-preview/table
        {:headers      (:headers sample)
         :rows         (:rows sample)
         :dark-mode?   dark-mode?
         :height       (str (max 100 (min (+ 3 (* 25 (inc (count (:rows sample))))) 300)) "px")
         :column-width 270
         :sortable?    true
         :filterable?  true
         :resizable?   true
         :row-numbers? true}])]))

(defn columns-list [{:keys [catalog schema table]}]
  (let [fetch-params {:object "columns"
                      :catalog catalog
                      :schema  schema
                      :table   table}
        columns    @(re-posh/subscribe [:sql-metadata-partial/prop [:sql-metadata-partial/params fetch-params]
                                        :sql-metadata-partial/metadata])]

    (react/useEffect
     (fn []
       (dispatch [:sql-console.api/fetch-metadata fetch-params])
       (fn []))
     #js [catalog schema table])

    [:div
     [docs-panel/section-title "Columns"]

     (if (empty? columns)
       "No data"
       [docs-panel/details
        [:div
         [ui.table/table {:style {"--background" "transparent"}
                          :class "text-sm"}
          [thead
           [tr
            [th "Column"]
            [th "Type"]]]
          [tbody
           (for [{:keys [column-name type-name]} columns]
             ^{:key column-name}
             [tr
              [td column-name]
              [td type-name]])]]]])]))

(defn unit-tests-table [{:keys [unit-tests]}]
  (let [[header-keys rows]
        (react/useMemo
         (fn []
           (let [header-keys (some-> unit-tests first keys vec)]
             [header-keys
              (mapv (fn [row] (mapv #(get row %) header-keys)) unit-tests)]))
         #js [unit-tests])
        dark-mode?  @(re-posh/subscribe [:app/prop :app/dark-mode?])]
    [:div {:class "col-span-2"}
     [docs-panel/section-title "Unit test results"]

     (if (empty? unit-tests)
       "No data"
       [:f> data-grid-preview/table
        {:headers      header-keys
         :rows         rows
         :dark-mode?   dark-mode?
         :height       (str (max 100 (min (+ 41 (* 26 (count unit-tests))) 300)) "px")
         :column-width 270
         :sortable?    true
         :filterable?  true
         :resizable?   true
         :row-numbers? true}])]))

(defn pick-group-dialog []
  (dispatch [:groups-panel/add])
  (dispatch [:group.api/fetch-groups {:items-per-page 1000}])

  (fn [{:keys [open set-open table-name]}]
    (let [groups   @(re-posh/subscribe [:group.subs/ordered-groups])
          loading? @(re-posh/subscribe [:mold-http/loading? :group.api/fetch-groups])]
      [dialog-root {:open           open
                    :on-open-change #(when-not % (set-open false))}
       [dialog-content
        [dialog-title "Pick a transform group"]
        [:div {:class "mt-4"}
         (if loading?
           [spinner-lucide]
           [:div {:class "flex flex-col border rounded-xs divide-y"}
            (for [{:group/keys [name id]} groups]
              ^{:key id}
              [:a {:href  (rt.easy/href :transforms
                                        {:group-id id}
                                        {:json-flattening true
                                         :table-name      table-name})
                   :class "px-4 py-2 cursor-pointer hover:bg-kleene-selected-background transition-colors"}
               name])])]]])))

(defn sql-generator-button []
  (let [open? (reagent/atom false)]
    (fn [{:keys [table-name]}]
      [:<>
       [button {:variant :empty
                :on-click #(reset! open? true)}
        "SQL Generator"]
       [pick-group-dialog {:open       @open?
                           :set-open   #(reset! open? %)
                           :table-name table-name}]])))

(defn last-updated-date [props]
  (let [timezone     @(re-posh/subscribe [:app.subs/timezone])
        last-altered (some-> (use-get-latest-update props)
                             :rows ffirst)]
    [date-tooltip {:date last-altered :timezone timezone}]))

(defn details-page [{:keys [classes] :as props}]
  (let [default-db     @(re-posh/subscribe [:connections.subs/warehouse-db-name])
        {:keys [catalog schema table] :as props} (update props :catalog #(or % default-db))

        table-data     @(re-posh/subscribe [:tables-metadata.subs/table props])
        timezone       @(re-posh/subscribe [:app.subs/timezone])
        loading?       @(re-posh/subscribe [:mold-http/loading? :tables-metadata.api/search])
        warehouse-type @(re-posh/subscribe [:warehouse-data/prop :warehouse-data/sink-type])
        snowflake?     (= warehouse-type "snowflake")

        columns-fetch-params {:object "columns" :catalog catalog :schema schema :table table}
        columns              @(re-posh/subscribe [:sql-metadata-partial/prop
                                                   [:sql-metadata-partial/params columns-fetch-params]
                                                   :sql-metadata-partial/metadata])
        columns-loading?     @(re-posh/subscribe [:mold-http/loading-id? :sql-console.api/fetch-metadata
                                                   (sql-console.api/->metadata-request-id columns-fetch-params)])

        {:keys [created row-count sources transforms unit-tests]} table-data]

    (react/useEffect
     (fn []
       (when (empty? table-data) (init-tables))
       (fn []))
     #js [props])

    (react/useEffect
     (fn []
       (dispatch [:sql-console.api/fetch-metadata columns-fetch-params])
       (fn []))
     #js [catalog schema table])

    [page-root {:variant :border
                :classes classes}
     [page-header {:variant :border
                   :class "-top-10 z-20"}
      [page-title
       [:span {:class "font-normal"} catalog "." schema "."] table]
      [page-controls
       [:div {:class "flex justify-between items-center gap-2"}
        [:div {:class "flex gap-2"}
         [button {:variant  :empty
                  :size     :icon-md
                  :icon     ArrowLeft
                  :on-click go-to-tables}]
         [button {:variant :empty
                  :disabled (or columns-loading? (empty? columns))
                  :on-click #(preview-in-console catalog schema table columns)}
          "SQL Console"]
         (when snowflake?
           [sql-generator-button {:table-name (->qualified-name catalog schema table)}])]
        [:div
         [button {:variant  :empty
                  :icon     RefreshCw
                  :on-click init-tables}
          "Refresh"]]]]]
     [page-content {:variant :border :class "overflow-visible"}
      (if loading?
        [:div {:class "w-full h-full flex items-center justify-center gap-2"}
         [spinner-lucide] "Loading..."]

        [docs-panel/panel
         [docs-panel/content {:class "grid grid-cols-[minmax(auto,1000px)_auto] grid-rows-[repeat(6,auto)] grid-flow-col mt-0"}
          [:div
           [docs-panel/section-title "Details"]
           [:ul {:class "text-sm"}
            [:li "Created on " [date-tooltip {:date created :timezone timezone}]]

            ;; This data is hard to get for BQ by using just sink-console request because of region requirement
            (when (not= "bigquery" warehouse-type)
              [:li "Updated on " [:f> last-updated-date props]])
            [:li "Row count: " row-count]]]

          [:f> extracts-list {:sources sources}]
          [:f> transforms-list {:transforms transforms}]
          [:f> sample-rows props]
          [:f> columns-list props]
          [:f> unit-tests-table {:unit-tests unit-tests}]]])]]))

(defn open-table-info-modal [{:keys [db schema table]}]
  (dispatch [:modals.events/set-modal-params "table-details" {:db db :schema schema :table table}])
  (dispatch [:modals.events/toggle-modal "table-details" true])
  (modal/bring-to-front "table-details"))

(defn modal []
  (react/useEffect
   (fn []
     (modal/medium-size "table-details")

     ;; return unmount callback
     (fn []))
   #js [])

  (let [{:keys [db schema table]} @(re-posh/subscribe [:modals.subs/modal-params "table-details"])]
    [:f> modal/modal {:id "table-details"
                      :header-props {:title "Table details"}
                      :open? @(re-posh/subscribe [:modals.subs/modal-open? "table-details"])
                      :on-close #(dispatch [:modals.events/toggle-modal "table-details" false])}

     [:f> details-page {:catalog (some-> db str/upper-case)
                        :schema  (some-> schema str/upper-case)
                        :table   (some-> table str/upper-case)
                        :classes {:root "h-auto min-h-0"}}]]))

(defn page []
  (let [query @(re-posh/subscribe [:app-route/prop :app-route/query])]
    (when query
      [:f> details-page {:catalog (:db query)
                         :schema  (:schema query)
                         :table   (:table query)}])))
